import { useState, useMemo } from 'react';
import type { SaleComp } from '../../types/calculations';

// Re-export for convenience
export type { SaleComp };

interface SaleCompsTableProps {
  comps: SaleComp[];
  subjectValue?: number; // For ARV comparison
  medianPrice?: number;
}

type SortKey = 'sale_price' | 'price_per_sqft' | 'sale_date' | 'distance_miles';
type SortDirection = 'asc' | 'desc';

export function SaleCompsTable({ comps, subjectValue, medianPrice }: SaleCompsTableProps) {
  const [expanded, setExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('distance_miles');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  if (!comps.length) return null;

  // Calculate median if not provided
  const calculatedMedian = useMemo(() => {
    if (medianPrice) return medianPrice;
    const prices = [...comps].map(c => c.sale_price).sort((a, b) => a - b);
    const mid = Math.floor(prices.length / 2);
    return prices.length % 2 === 0
      ? (prices[mid - 1] + prices[mid]) / 2
      : prices[mid];
  }, [comps, medianPrice]);

  // ARV comparison
  const arvComparison = useMemo(() => {
    if (!subjectValue || !calculatedMedian) return null;
    const diff = subjectValue - calculatedMedian;
    const percent = (diff / calculatedMedian) * 100;
    return { diff, percent };
  }, [subjectValue, calculatedMedian]);

  // Sorted comps
  const sortedComps = useMemo(() => {
    return [...comps].sort((a, b) => {
      let aVal: number | string | undefined;
      let bVal: number | string | undefined;

      switch (sortKey) {
        case 'sale_price':
          aVal = a.sale_price;
          bVal = b.sale_price;
          break;
        case 'price_per_sqft':
          aVal = a.price_per_sqft ?? 0;
          bVal = b.price_per_sqft ?? 0;
          break;
        case 'sale_date':
          aVal = a.sale_date;
          bVal = b.sale_date;
          break;
        case 'distance_miles':
          aVal = a.distance_miles ?? 999;
          bVal = b.distance_miles ?? 999;
          break;
      }

      if (aVal === undefined || bVal === undefined) return 0;
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [comps, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const toggleRow = (idx: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <span className="text-gray-300 ml-1">&#8597;</span>;
    return sortDirection === 'asc'
      ? <span className="text-blue-500 ml-1">&#8593;</span>
      : <span className="text-blue-500 ml-1">&#8595;</span>;
  };

  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-sm text-green-600 hover:text-green-800 flex items-center gap-1 font-medium"
      >
        <span
          className="transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          &#9654;
        </span>
        Sale Comparables ({comps.length})
      </button>

      {expanded && (
        <div className="mt-3">
          {/* Summary Row */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <div className="flex justify-between items-center text-sm">
              <div>
                <span className="text-gray-600">Median Sale Price:</span>
                <span className="font-semibold text-gray-900 ml-2">
                  ${calculatedMedian.toLocaleString()}
                </span>
              </div>
              {arvComparison && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Your ARV vs Median:</span>
                  <span
                    className={`font-semibold ${
                      arvComparison.percent > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {arvComparison.percent > 0 ? '+' : ''}{arvComparison.percent.toFixed(1)}%
                    ({arvComparison.diff > 0 ? '+' : ''}${Math.abs(arvComparison.diff).toLocaleString()})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left text-sm font-medium text-gray-500">#</th>
                <th className="px-2 py-2 text-left text-sm font-medium text-gray-500">Address</th>
                <th
                  className="px-2 py-2 text-right text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sale_price')}
                >
                  Sale Price <SortIcon column="sale_price" />
                </th>
                <th
                  className="px-2 py-2 text-right text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price_per_sqft')}
                >
                  $/SqFt <SortIcon column="price_per_sqft" />
                </th>
                <th
                  className="px-2 py-2 text-center text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sale_date')}
                >
                  Date <SortIcon column="sale_date" />
                </th>
                <th
                  className="px-2 py-2 text-right text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('distance_miles')}
                >
                  Distance <SortIcon column="distance_miles" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedComps.map((comp, i) => (
                <>
                  <tr
                    key={`row-${i}`}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleRow(i)}
                  >
                    <td className="px-2 py-2 text-gray-600">{i + 1}</td>
                    <td className="px-2 py-2 truncate max-w-[180px] text-gray-700" title={comp.address}>
                      <span className="mr-1 text-gray-400 text-sm">
                        {expandedRows.has(i) ? '▼' : '▶'}
                      </span>
                      {comp.address}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gray-700">
                      ${comp.sale_price.toLocaleString()}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-600">
                      {comp.price_per_sqft ? `$${comp.price_per_sqft.toFixed(0)}` : '-'}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-600">
                      {formatDate(comp.sale_date)}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-600">
                      {comp.distance_miles !== undefined ? `${comp.distance_miles.toFixed(2)}mi` : '-'}
                    </td>
                  </tr>
                  {/* Expanded Details Row */}
                  {expandedRows.has(i) && (
                    <tr key={`details-${i}`} className="bg-gray-50">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Beds/Baths:</span>
                            <span className="ml-1 font-medium">
                              {comp.bedrooms ?? '-'}BR / {comp.bathrooms ?? '-'}BA
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">SqFt:</span>
                            <span className="ml-1 font-medium">
                              {comp.sqft ? comp.sqft.toLocaleString() : '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Year Built:</span>
                            <span className="ml-1 font-medium">
                              {comp.year_built ?? '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Full Date:</span>
                            <span className="ml-1 font-medium">
                              {comp.sale_date || '-'}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
